import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Mail, Send, Users, Plus, Trash2, RefreshCw, UserPlus, 
  BarChart3, Calendar, CheckCircle, XCircle, Car, Settings, X
} from 'lucide-react';
import { newsletterApi, carsApi, settingsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

export default function AdminNewsletter() {
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [activeTab, setActiveTab] = useState('subscribers');
  
  // Add subscriber form
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  
  // Send newsletter form
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [includeNewCars, setIncludeNewCars] = useState(true);
  const [sending, setSending] = useState(false);
  const [cars, setCars] = useState([]);
  const [selectedCarIds, setSelectedCarIds] = useState([]);
  
  // Email Settings Modal
  const [showEmailSettings, setShowEmailSettings] = useState(false);
  const [newsletterFromEmail, setNewsletterFromEmail] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    loadData();
  }, [showInactive]);

  const loadData = async () => {
    try {
      const [subsRes, statsRes, logsRes, carsRes, settingsRes] = await Promise.all([
        newsletterApi.getSubscribers(!showInactive),
        newsletterApi.getStats(),
        newsletterApi.getLogs(),
        carsApi.getAll({ is_active: true }),
        settingsApi.get()
      ]);
      setSubscribers(subsRes.data);
      setStats(statsRes.data);
      setLogs(logsRes.data);
      setCars(carsRes.data);
      // Load email settings
      const siteData = settingsRes.data?.data || {};
      setNewsletterFromEmail(siteData.newsletter_from_email || siteData.site_email || siteData.contact_email || '');
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    if (!newEmail) return;
    
    setAdding(true);
    try {
      await newsletterApi.addSubscriber(newEmail, newName || null);
      toast.success('Abonnent hinzugefügt');
      setNewEmail('');
      setNewName('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveSubscriber = async (id, email) => {
    if (!confirm(`Möchten Sie ${email} wirklich löschen?`)) return;
    
    try {
      await newsletterApi.removeSubscriber(id);
      toast.success('Abonnent gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleSendNewsletter = async (e) => {
    e.preventDefault();
    if (!subject || !content) {
      toast.error('Betreff und Inhalt erforderlich');
      return;
    }
    
    if (!confirm(`Newsletter an ${stats?.active || 0} Abonnenten senden?`)) return;
    
    setSending(true);
    try {
      const result = await newsletterApi.send(subject, content, includeNewCars, selectedCarIds);
      toast.success(result.data.message);
      setSubject('');
      setContent('');
      setSelectedCarIds([]);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };
  
  const handleSaveEmailSettings = async () => {
    setSavingSettings(true);
    try {
      // Get current settings and update only the newsletter_from_email
      const currentSettings = await settingsApi.get();
      const updatedData = {
        ...currentSettings.data?.data,
        newsletter_from_email: newsletterFromEmail || null
      };
      await settingsApi.update(updatedData);
      toast.success('E-Mail-Einstellungen gespeichert');
      setShowEmailSettings(false);
    } catch (error) {
      toast.error('Fehler beim Speichern der Einstellungen');
    } finally {
      setSavingSettings(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSourceLabel = (source) => {
    const labels = {
      website: 'Website',
      inquiry: 'Anfrage',
      admin: 'Manuell'
    };
    return labels[source] || source;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-newsletter" className="max-w-6xl">
      {/* Email Settings Modal */}
      {showEmailSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading text-lg flex items-center gap-2">
                <Settings size={18} />
                Newsletter E-Mail-Einstellungen
              </h3>
              <button 
                onClick={() => setShowEmailSettings(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="overline block mb-2">Absender E-Mail-Adresse</label>
                <input
                  type="email"
                  value={newsletterFromEmail}
                  onChange={(e) => setNewsletterFromEmail(e.target.value)}
                  placeholder="newsletter@ihre-domain.at"
                  className="input-premium w-full"
                  data-testid="newsletter-from-email"
                />
                <p className="text-xs text-[#666666] mt-2">
                  Diese E-Mail-Adresse wird als Absender für alle Newsletter verwendet.
                  Lassen Sie das Feld leer, um die Standard-E-Mail aus den Einstellungen zu verwenden.
                </p>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  onClick={() => setShowEmailSettings(false)}
                  className="btn-secondary"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleSaveEmailSettings}
                  className="btn-primary flex items-center gap-2"
                  disabled={savingSettings}
                  data-testid="save-email-settings-btn"
                >
                  {savingSettings ? <RefreshCw size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                  Speichern
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-light">Newsletter</h1>
          <p className="text-sm text-[#666666] mt-1">Verwalten Sie Ihre Newsletter-Abonnenten</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowEmailSettings(true)}
            className="btn-secondary flex items-center gap-2"
            title="E-Mail-Einstellungen"
            data-testid="newsletter-email-settings-btn"
          >
            <Settings size={16} />
          </Button>
          <Button
            onClick={loadData}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white p-4 border border-[#E5E5E5] rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <Users size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-heading">{stats?.active || 0}</p>
              <p className="text-xs text-[#666666]">Aktive Abonnenten</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 border border-[#E5E5E5] rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-lg">
              <XCircle size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-heading">{stats?.unsubscribed || 0}</p>
              <p className="text-xs text-[#666666]">Abgemeldet</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 border border-[#E5E5E5] rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Mail size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-heading">{stats?.by_source?.website || 0}</p>
              <p className="text-xs text-[#666666]">Via Website</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 border border-[#E5E5E5] rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 rounded-lg">
              <CheckCircle size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-heading">{stats?.by_source?.inquiry || 0}</p>
              <p className="text-xs text-[#666666]">Via Anfragen</p>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 bg-[#F9F9F7] p-1 rounded-lg">
          <TabsTrigger value="subscribers" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Users size={16} />
            Abonnenten
          </TabsTrigger>
          <TabsTrigger value="send" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Send size={16} />
            Newsletter senden
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Calendar size={16} />
            Verlauf
          </TabsTrigger>
        </TabsList>

        {/* Subscribers Tab */}
        <TabsContent value="subscribers" className="space-y-6">
          {/* Add Subscriber Form */}
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
              <UserPlus size={18} />
              Abonnent hinzufügen
            </h3>
            <form onSubmit={handleAddSubscriber} className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="E-Mail-Adresse"
                className="input-premium flex-1"
                required
                data-testid="new-subscriber-email"
              />
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Name (optional)"
                className="input-premium flex-1"
              />
              <Button
                type="submit"
                className="btn-primary flex items-center gap-2"
                disabled={adding}
                data-testid="add-subscriber-btn"
              >
                {adding ? <RefreshCw size={16} className="animate-spin" /> : <Plus size={16} />}
                Hinzufügen
              </Button>
            </form>
          </div>

          {/* Subscribers List */}
          <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
            <div className="p-4 border-b border-[#E5E5E5] flex justify-between items-center">
              <h3 className="font-heading text-lg">
                Abonnenten ({subscribers.length})
              </h3>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="w-4 h-4"
                />
                Auch Abgemeldete zeigen
              </label>
            </div>
            
            {subscribers.length === 0 ? (
              <div className="p-8 text-center text-[#666666]">
                <Mail size={40} className="mx-auto mb-4 opacity-50" />
                <p>Noch keine Abonnenten</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F9F9F7]">
                    <tr>
                      <th className="text-left p-4 overline">E-Mail</th>
                      <th className="text-left p-4 overline">Name</th>
                      <th className="text-left p-4 overline">Quelle</th>
                      <th className="text-left p-4 overline">Angemeldet am</th>
                      <th className="text-left p-4 overline">Status</th>
                      <th className="text-right p-4 overline">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((sub) => (
                      <tr key={sub.id} className="border-t border-[#E5E5E5] hover:bg-[#F9F9F7]">
                        <td className="p-4">{sub.email}</td>
                        <td className="p-4 text-[#666666]">{sub.name || '-'}</td>
                        <td className="p-4">
                          <span className="px-2 py-1 text-xs bg-[#F9F9F7] rounded">
                            {getSourceLabel(sub.source)}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-[#666666]">{formatDate(sub.subscribed_at)}</td>
                        <td className="p-4">
                          {sub.is_active ? (
                            <span className="px-2 py-1 text-xs bg-green-50 text-green-700 rounded">Aktiv</span>
                          ) : (
                            <span className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded">Abgemeldet</span>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          <button
                            onClick={() => handleRemoveSubscriber(sub.id, sub.email)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                            title="Löschen"
                            data-testid={`delete-subscriber-${sub.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Send Newsletter Tab */}
        <TabsContent value="send" className="space-y-6">
          <div className="bg-white p-6 border border-[#E5E5E5] rounded-lg">
            <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
              <Send size={18} />
              Newsletter erstellen
            </h3>
            
            <form onSubmit={handleSendNewsletter} className="space-y-6">
              <div>
                <label className="overline block mb-2">Betreff</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Neue Premium-Fahrzeuge diese Woche"
                  className="input-premium w-full"
                  required
                  data-testid="newsletter-subject"
                />
              </div>
              
              <div>
                <label className="overline block mb-2">Inhalt</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Liebe Kunden,&#10;&#10;diese Woche haben wir neue exklusive Fahrzeuge für Sie..."
                  className="input-premium w-full h-48 resize-none"
                  required
                  data-testid="newsletter-content"
                />
              </div>
              
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeNewCars}
                    onChange={(e) => setIncludeNewCars(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Neue Fahrzeuge der letzten 7 Tage automatisch einfügen</span>
                </label>
              </div>
              
              {/* Car Selection */}
              <div>
                <label className="overline block mb-2">Zusätzliche Fahrzeuge auswählen (optional)</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-h-60 overflow-y-auto p-2 bg-[#F9F9F7] rounded">
                  {cars.slice(0, 12).map((car) => (
                    <label
                      key={car.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        selectedCarIds.includes(car.id) ? 'bg-gold/20 border border-gold' : 'bg-white border border-[#E5E5E5]'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCarIds.includes(car.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCarIds([...selectedCarIds, car.id]);
                          } else {
                            setSelectedCarIds(selectedCarIds.filter(id => id !== car.id));
                          }
                        }}
                        className="hidden"
                      />
                      <Car size={14} className="text-[#666666]" />
                      <span className="text-xs truncate">{car.brand} {car.model}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-[#E5E5E5]">
                <p className="text-sm text-[#666666]">
                  Newsletter wird an <strong>{stats?.active || 0}</strong> aktive Abonnenten gesendet
                </p>
                <Button
                  type="submit"
                  className="btn-primary flex items-center gap-2"
                  disabled={sending || !stats?.active}
                  data-testid="send-newsletter-btn"
                >
                  {sending ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Newsletter senden
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
          
          {/* Info Box */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>💡 Tipp:</strong> Wöchentliche Newsletter mit neuen Fahrzeugen sind am effektivsten. 
              Die E-Mails enthalten automatisch einen Abmelde-Link für jeden Empfänger.
            </p>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
            <div className="p-4 border-b border-[#E5E5E5]">
              <h3 className="font-heading text-lg">Versand-Verlauf</h3>
              <p className="text-sm text-[#666666] mt-1">Newsletter-Statistiken mit Öffnungsrate</p>
            </div>
            
            {logs.length === 0 ? (
              <div className="p-8 text-center text-[#666666]">
                <Calendar size={40} className="mx-auto mb-4 opacity-50" />
                <p>Noch keine Newsletter versendet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F9F9F7]">
                    <tr>
                      <th className="text-left p-4 overline">Datum</th>
                      <th className="text-left p-4 overline">Betreff</th>
                      <th className="text-left p-4 overline">Gesendet</th>
                      <th className="text-left p-4 overline">Geöffnet</th>
                      <th className="text-left p-4 overline">Öffnungsrate</th>
                      <th className="text-left p-4 overline">Fehler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id} className="border-t border-[#E5E5E5] hover:bg-[#F9F9F7]">
                        <td className="p-4 text-sm">{formatDate(log.sent_at)}</td>
                        <td className="p-4 font-medium">{log.subject}</td>
                        <td className="p-4">
                          <span className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded">
                            {log.sent_count}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="px-2 py-1 text-xs bg-green-50 text-green-700 rounded">
                            {log.opened_count || 0}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gold transition-all" 
                                style={{ width: `${Math.min(log.open_rate || 0, 100)}%` }}
                              />
                            </div>
                            <span className={`text-sm font-medium ${
                              (log.open_rate || 0) >= 30 ? 'text-green-600' : 
                              (log.open_rate || 0) >= 15 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {log.open_rate || 0}%
                            </span>
                          </div>
                        </td>
                        <td className="p-4">
                          {log.failed_count > 0 ? (
                            <span className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded">
                              {log.failed_count}
                            </span>
                          ) : (
                            <span className="text-[#999999]">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Open Rate Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>📊 Öffnungsrate:</strong> Zeigt an, wie viele Empfänger den Newsletter tatsächlich geöffnet haben. 
              Durchschnittliche Öffnungsraten liegen bei 15-25%. Werte über 30% sind sehr gut.
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
